import { z } from 'zod/v3';
import { IconCategorySchema, IconTypeSchema } from './generated/enums';

/**
 * Interface defining the structure of a MiniTopic
 */
export interface MiniTopic {
  id: string;
  parentId?: string | null;
  type: 'SECTION' | 'GROUP' | 'API_SECTION' | 'CHANGELOG_SECTION' | 'GUIDE' | 'CHANGELOG_INDEX' | 'CHANGELOG';
  title: string;
  description: string;
  tags?: string[];
  confidence?: number;
  subtopics?: MiniTopic[];
  canonicalSlug?: string;
  isSuggested?: boolean;
  hasArticle?: boolean;
  order?: number; 
  docId?: string;
  pageIcon?: string | null;
  icon?: string | null;
  iconCategory?: 'EMOJI' | 'LUCIDE' | null;
  iconType?: 'REGULAR' | 'SOLID' | 'LIGHT' | 'THIN' | 'SHARP_SOLID' | 'DUOTONE' | 'BRANDS' | null;
  iconColor?: string | null;
  showInNavigation?: boolean;
  showInSite?: boolean;
  syncedProviders?: string[];
  sectionConfig?: any; // Section configuration for nested sections
  isPlaceholder?: boolean; // Indicates this topic is a placeholder for future import
  metadata?: Record<string, any>; // Additional metadata for the topic
}

export enum MiniTopicType {
  SECTION = 'SECTION',
  GROUP = 'GROUP',
  GUIDE = 'GUIDE',
  API_SECTION = 'API_SECTION',
  CHANGELOG_SECTION = 'CHANGELOG_SECTION',
  CHANGELOG_INDEX = 'CHANGELOG_INDEX',
  CHANGELOG = 'CHANGELOG',
}
/**
 * Zod schema for MiniTopic with proper recursive typing
 */
export const MiniTopicSchema: z.ZodType<MiniTopic> = z.lazy(() =>
  z.object({
    id: z.string(),
    parentId: z.string().optional(),
    type: z.enum(['SECTION', 'API_SECTION', 'CHANGELOG_SECTION', 'GUIDE', 'CHANGELOG_INDEX', 'CHANGELOG'] as const),
    title: z.string(),
    description: z.string(),
    tags: z.array(z.string()).optional(),
    confidence: z.number().optional(),
    subtopics: z.array(MiniTopicSchema).optional(),
    canonicalSlug: z.string().optional(),
    isSuggested: z.boolean().optional(),
    hasArticle: z.boolean().optional(),
    order: z.number().optional(),
    docId: z.string().optional(),
    pageIcon: z.string().nullable().optional(),
    icon: z.string().nullable().optional(),
    iconCategory: IconCategorySchema.nullable().optional(),
    iconType: IconTypeSchema.nullable().optional(),
    iconColor: z.string().nullable().optional(),
    showInNavigation: z.boolean().optional(),
    showInSite: z.boolean().optional(),
    syncedProviders: z.array(z.string()).optional(),
    sectionConfig: z.any().optional(), // Section configuration for nested sections
    isPlaceholder: z.boolean().optional(),
    metadata: z.record(z.any()).optional(),
  })
)


// ParsedHtmlStructure interface (complete version to match usage in topic-generation.ts)
export interface ParsedHtmlStructure {
  title?: string;
  headings: Array<{ level: number; text: string; id?: string }>;
  navigation: Array<{ text: string; url: string; isActive: boolean }>;
  forms: Array<{ 
    id?: string;
    name?: string;
    action?: string;
    method?: string;
    fields: Array<{
      name: string;
      type: string;
      label: string;
      required: boolean;
      options?: string[];
      placeholder?: string;
      value?: string;
    }>;
  }>;
  interactiveElements: Array<{ type: string; text: string; selector: string; url?: string }>;
  sections?: Array<{heading: string; content: string}>;
  mainContent?: string;
  metadata?: Record<string, string>;
  contentStructure?: {
    main: any | null;
    sidebar?: any | null;
    header?: any | null;
    footer?: any | null;
  };
  dialogs?: Array<{
    id?: string;
    title?: string;
    content: string;
    selector: string;
    isModal?: boolean;
  }>;
  popovers?: Array<{
    id?: string;
    title?: string;
    content: string;
    trigger?: string;
    selector: string;
  }>;
}

/**
 * Schemas related to topics, glossary and documentation
 * Separated to avoid circular dependencies
 */
// Topic hierarchy schema
export const TopicHierarchySchema = z.object({
  topics: z.array(MiniTopicSchema),
});

export type TopicHierarchy = z.infer<typeof TopicHierarchySchema>;

// Input schemas
export const TopicInputSchema = z.object({
  instructions: z
    .string()
    .describe(
      "Key instructions to have in mind while generating the outline",
    ),
  keyInsights: z
    .array(z.string())
    .describe(
      "An array of key insights to make sure to highlight and include in the outline",
    ),
  glossary: z.array(z.string()).describe("Glossary terms of the application domain"),
});

export type TopicInput = z.infer<typeof TopicInputSchema>;

export const GlossaryInputSchema = z.object({
  input: z.array(z.string()).describe("Input to generate the glossary from"),
});

export type GlossaryInput = z.infer<typeof GlossaryInputSchema>;

export const DocumentationSchema = z.object({
  title: z.string(),
  description: z.string(),
  topics: z.array(MiniTopicSchema),
});


// Options for topic generation - this unified options interface combines all the different options
// from various parts of the system
export interface TopicGenerationOptions {
  // Basic topic generation options
  maxDepth?: number;
  includeStepIds?: boolean;
  includeHtmlStructure?: boolean;
  pageStructures?: Record<string, ParsedHtmlStructure>;
  
  // Topic summary options
  maxTopicDepth?: number;
  minStepsPerTopic?: number;
  style?: string;
} 
